import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Resizable } from 're-resizable';
import axios from "axios";
import dayjs from "dayjs";

import {
  generateEmailDigestService,
  getDigestByTitleService,
  getSavedDigestService,
  addDigestService,
  updateDigestService,
  getDigestEmailsService,
  addDigestEmailsService,
  updateDigestEmailsService,
  deleteEmailActionByTypeService
} from "../../utils/digestService";
import { getEmailContentService, restoreEmailToInboxService } from "../../utils/emailService";
import { deleteTaskService, deleteTaskFileService } from "../../utils/taskService";
import { deleteChatService, getChatsByEmailIdsService } from "../../utils/chatService";
import { getUserEmailActionReadOnlyService, updateUserEmailActionReadOnlyService } from "../../utils/userService";
import {
  addOrSyncDigest,
  selectDigestForRoute,
  selectDigestByTitleForMailbox,
  updateDigest,
  setCurrentDigest,
} from "../../reducers/digestReducer";
import { createTask, setCurrentTask, deleteTask } from "../../reducers/taskReducer";
import { setCurrentProject } from "../../reducers/projectReducer";
import { addEmailFileId, deleteThread, createEmailThread } from "../../reducers/chatReducer";
import { useIterativeDigestEmailActions } from "../../hooks/useIterativeDigestEmailActions";
import useEventCallback from "../../hooks/useEventCallback";
import {
  replyToEmail,
  archiveEmail,
  moveEmail,
  createEmailTask,
  addEmailToProject,
  getAssistantActions,
} from "../../utils/emailActions";
import { formatEmailDate } from "../../utils/formatter";
import { mergeEmailContentResponse } from "../../utils/emailContentMerge";
import { shouldHydrateDigestDestinations } from "../../utils/digestDestinations";
import { resolveActionDestination } from "../../utils/actionDestination";
import {
  MAIL_WRITE_BLOCKED_EVENT,
  canWriteMail,
  getMailWriteContext,
  refuseMailWrite,
} from "../../utils/mailProvider";
import {
  pickSafeMailFailureFields,
  presentMailFailure,
} from "../../utils/mailFailurePresentation";
import { runMailFailureRecovery } from "../../utils/authUtils";
import EmailContent from "./EmailContent";
import FilteredEmailView from "./FilteredEmailView";
import DigestEmailSummary from "./DigestEmailSummary";
import DigestDatePicker from './DigestDatePicker'
import EmailTriageOnboardingModal from './EmailTriageOnboardingModal'

export default function Digest() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getToken, userId } = useAuth();
  const { user } = useUser();

  const { digestDate } = useParams();
  const generateEmailDigestStarted = useRef(false);
  const savedLoadRef = useRef(null);
  const getUserEmailActionReadOnlyCalled = useRef(false)


  const { currentDigest, digests } = useSelector(
    (state) => state.digestList || { currentDigest: null, digests: [] }
  );
  const routedDigestTitle = digestDate
    ? dayjs(digestDate).format("MMMM D, YYYY")
    : null;
  const routedDigest = routedDigestTitle
    ? selectDigestForRoute(digests, routedDigestTitle, currentDigest?.mailbox?.id)
    : null;
  const getDigestMailIntegrationId = () => (
    routedDigest?.mailbox?.id ?? currentDigest?.mailbox?.id
  );
  const viewKey = `${userId ?? ''}|${digestDate ?? ''}|${getDigestMailIntegrationId() ?? ''}`;
  const viewKeyRef = useRef(viewKey);
  const viewGenerationRef = useRef(0);
  if (viewKeyRef.current !== viewKey) viewGenerationRef.current += 1;
  viewKeyRef.current = viewKey;
  useEffect(() => {
    viewKeyRef.current = viewKey;
    savedLoadRef.current = null;
    setIsLoadingDigest(false);
    setSavedLoadError(false);
    setSyncError(null);
    return () => {
      viewKeyRef.current = null;
      viewGenerationRef.current += 1;
    };
  }, [viewKey]);
  const captureCurrentView = () => {
    const generation = viewGenerationRef.current;
    const key = viewKeyRef.current;
    return () => viewKeyRef.current === key && viewGenerationRef.current === generation;
  };

  const todaysDigest = useSelector((state) => {
    return selectDigestByTitleForMailbox(
      state.digestList?.digests,
      dayjs().format("MMMM D, YYYY"),
      currentDigest?.mailbox?.id,
    );
  });

  const chats = useSelector(state => state.chats || []);
  
  const [isLoadingDigest, setIsLoadingDigest] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [savedLoadError, setSavedLoadError] = useState(false);
  const [syncFailureVersion, setSyncFailureVersion] = useState(0);
  const [isRecoveringMailFailure, setIsRecoveringMailFailure] = useState(false);
  const mailRecoveryStarted = useRef(false);
  const [estimatedEmailTime, setEstimatedEmailTime] = useState(0);

  // Group emails by threadId
  const groupedEmails = currentDigest?.content?.reduce((acc, email) => {
    if (!acc[email.threadId]) {
      acc[email.threadId] = [];
    }
    acc[email.threadId].push(email);
    // Sort emails within each thread by date, oldest first
    acc[email.threadId].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });
    return acc;
  }, {});

  // Convert grouped emails into a simple array of threads
  const itemsToRender = groupedEmails
    ? Object.values(groupedEmails)
        .sort((a, b) => {
          // Sort threads by date, newest first
          const dateA = new Date(a[0].date);
          const dateB = new Date(b[0].date);
          return dateB - dateA;
        })
        .sort((a, b) => {
          // Move archived threads to the bottom
          if (a[0].archived && !b[0].archived) return 1;
          if (!a[0].archived && b[0].archived) return -1;
          return 0;
        })
    : [];

  const {
    processAllEmails,
    getAllEmails,
    isProcessingEmailActions,
    isProcessingEvents,
    currentEmailProcessingIndex,
  } = useIterativeDigestEmailActions({ itemsToRender, currentDigest });

  const [inputValues, setInputValues] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsVisible] = useState({});
  const [isCardVisible, setIsCardVisible] = useState({});
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState('success');
  const notificationTimerRef = useRef(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [isDigestSummaryOpen, setIsDigestSummaryOpen] = useState(true)
  const [readOnlyMode, setReadOnlyMode] = useState(true) // Default ON for safety
  const [unknownReadOnlyMode, setUnknownReadOnlyMode] = useState(false)
  const [isEmailTriageOnboardingModalOpen, setIsEmailTriageOnboardingModalOpen] = useState(false)

  // Resizable container - temporary location
  // TODO: This is a temporary solution. Need to move this to shareable component
  const [containerWidth, setContainerWidth] = useState(0);
  const [leftWidth, setLeftWidth] = useState(0);
  const containerRef = useRef(null);
  const MIN_LEFT_WIDTH = 300; // Minimum width for left panel in pixels
  const MIN_LEFT_PERCENTAGE = 0.25; // Minimum percentage of total width
  const DEFAULT_LEFT_RATIO = .4; // Default left panel takes 2/5 of the screen

  const remainingDigestItems = currentDigest?.content?.filter(
    (email) => !email.actions || email.actions?.length === 0
  ).length;
  const totalDigestItems = currentDigest?.content?.length;

  const showNotification = (message, severity = 'success') => {
    setNotificationMessage(message);
    setNotificationSeverity(severity);
    setIsNotificationVisible(true);
    clearTimeout(notificationTimerRef.current);
    notificationTimerRef.current = setTimeout(() => {
      setIsNotificationVisible(false);
      setNotificationMessage('');
    }, severity === 'error' ? 6000 : 3000);
  };

  useEffect(() => () => clearTimeout(notificationTimerRef.current), []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const getIsMobileOnly = () => {
    return windowWidth < 768
  }

  const synchronizeTodaysDigest = useEventCallback(() => {
    const currentDate = dayjs().format("MMMM D, YYYY");
    const serverHeadSource = todaysDigest?.serverHead?.source || "";

    // If digestDate param is passed and is not today, skip this effect
    if (!userId || (digestDate && !dayjs(digestDate).isSame(dayjs(), 'day'))) return;

    // if digestDate param is not passed and todaysDigest doesn't exist, generate digest
    if (!todaysDigest?.title && !generateEmailDigestStarted.current) {
      getTodaysEmailDigest();
    }
    // If todaysDigest exist and is not for the current date, generate digest
    else if (todaysDigest?.title && todaysDigest.title !== currentDate) {
      getTodaysEmailDigest();
    }
    else if (shouldHydrateDigestDestinations(todaysDigest)) {
      getTodaysEmailDigest();
    }
    // If todaysDigest exist and is for the current date, 
    // but content is empty or serverHead source is cron, fetch today's digest
    else if (!Array.isArray(todaysDigest?.content) || serverHeadSource === "schedule") {
      getTodaysEmailDigest();
    }
    // If digestDate param is passed and is today, and content is not empty, set current digest
    else {
      dispatch(setCurrentDigest(todaysDigest));
    }
  });

  useEffect(() => {
    synchronizeTodaysDigest();
  }, [
    digestDate,
    synchronizeTodaysDigest,
    todaysDigest?.id,
    currentDigest?.mailbox?.id,
    todaysDigest?.serverHead?.lastEventAt,
    userId,
  ]);

  useEffect(() => {
    if (isProcessingEmailActions) {
      const remainingEmails = getAllEmails().length - currentEmailProcessingIndex;
      const estimatedSeconds = remainingEmails * 10;
      const estimatedMinutes = Math.ceil(estimatedSeconds / 60);
      setEstimatedEmailTime(estimatedMinutes);
    }
  }, [isProcessingEmailActions, currentEmailProcessingIndex, getAllEmails]);
  
  // Resizable container - effects and handlers
  // TODO: This is a temporary solution. Need to move this to shareable component
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        setLeftWidth(width * DEFAULT_LEFT_RATIO);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const loadSelectedDigest = useEventCallback(() => {
    const setDate = digestDate;
    const mailIntegrationId = getDigestMailIntegrationId();
    const selectedDigestChanged = routedDigest
      ? routedDigest.id !== currentDigest?.id
      : routedDigestTitle !== currentDigest?.title;

    if (setDate && mailIntegrationId && selectedDigestChanged) {
      getDigestByDate(setDate, mailIntegrationId);
    }
  });

  useEffect(() => {
    loadSelectedDigest();
  }, [
    digestDate,
    loadSelectedDigest,
    currentDigest?.id,
    routedDigest?.id,
    routedDigest?.mailbox?.id,
  ]);

  useEffect(() => {
    fetchUserEmailActionReadOnly()
  }, [])

  useEffect(() => {
    const showMailWriteBlockedNotification = (event) => {
      showNotification(event.detail?.message || 'This mail action is unavailable.', 'error');
    };

    window.addEventListener(MAIL_WRITE_BLOCKED_EVENT, showMailWriteBlockedNotification);
    return () => {
      window.removeEventListener(MAIL_WRITE_BLOCKED_EVENT, showMailWriteBlockedNotification);
    };
  }, []);

  const fetchUserEmailActionReadOnly = async () => {

    if (getUserEmailActionReadOnlyCalled.current) return;
    getUserEmailActionReadOnlyCalled.current = true;

    try {
      const userEmailActionReadOnly = await getUserEmailActionReadOnlyService()

      setUnknownReadOnlyMode(
        userEmailActionReadOnly && typeof userEmailActionReadOnly.enabled === "boolean"
          ? false
          : true
      )
      
      setReadOnlyMode(
        userEmailActionReadOnly && typeof userEmailActionReadOnly.enabled === "boolean"
          ? userEmailActionReadOnly.enabled
          : true
      )
    } catch (error) {
      console.error("Error fetching user email action read only:", error)
    } finally {
      getUserEmailActionReadOnlyCalled.current = false;
    }
  }


  // Handle resize events
  // TODO: This is a temporary solution. Need to move this to shareable component
  const handleResizeStop = (e, direction, ref, d) => {
    const newWidth = leftWidth + d.width;
    
    // Apply minimum width constraints
    if (newWidth >= MIN_LEFT_WIDTH && newWidth >= containerWidth * MIN_LEFT_PERCENTAGE) {
      setLeftWidth(newWidth);
    }
  };

  const loadDigest = async (digestResponse, isCurrent = () => true) => {
    try {
      setIsLoadingDigest(true);

      const emails = await getDigestEmailsService(getToken, digestResponse.id);
      if (!isCurrent()) return;
      const chats = await getChatsByEmailIdsService(getToken, emails.map(email => email.id));
      if (!isCurrent()) return;
      const loadedDigest = { ...digestResponse, content: emails };
      setIsLoadingDigest(false);
      setSyncError(null);
      dispatch(addOrSyncDigest(loadedDigest));
      chats.forEach(chat => {
      dispatch(createEmailThread({ chatId: chat.id, emailId: chat.emailId, mailIntegrationId: chat.mailIntegrationId, threadId: chat.threadId }));
    });
    } catch (error) {
      console.error('Error loading digest:', error);
      throw error;
    } finally {
      if (isCurrent()) setIsLoadingDigest(false);
    }
  }

  const getTodaysEmailDigest = async () => {
    const key = viewKeyRef.current;
    const loadKey = `${key}|${todaysDigest?.serverHead?.lastEventAt ?? ''}`;
    if (!userId || !key || savedLoadRef.current?.key === loadKey) return;
    const request = { key: loadKey };
    savedLoadRef.current = request;
    const isCurrent = captureCurrentView();
    const today = dayjs().format("MMMM D, YYYY");
    let mailIntegrationId = getDigestMailIntegrationId();
    setIsLoadingDigest(true);
    setSavedLoadError(false);
    try {
      let digestResponse;
      if (mailIntegrationId) {
        digestResponse = await getDigestByTitleService(getToken, userId, today, mailIntegrationId);
      } else {
        const saved = await getSavedDigestService(today);
        mailIntegrationId = saved?.mailIntegrationId;
        digestResponse = saved?.digest;
      }
      if (!isCurrent()) return;
      if (!mailIntegrationId || digestResponse === undefined) throw new Error('Saved digest lookup is incomplete');
      if (digestResponse) {
        if (digestResponse.mailbox?.id !== mailIntegrationId) throw new Error('Saved digest mailbox mismatch');
        await loadDigest(digestResponse, isCurrent);
      } else {
        await generateEmailDigest({ mailIntegrationId, isCurrent });
      }
    } catch (error) {
      if (!isCurrent()) return;
      setSavedLoadError(true);
      setSyncError({ code: null });
    } finally {
      if (isCurrent()) setIsLoadingDigest(false);
    }
  }

  const retrySavedDigest = () => {
    savedLoadRef.current = null;
    if (digestDate && !dayjs(digestDate).isSame(dayjs(), 'day')) {
      return getDigestByDate(digestDate, getDigestMailIntegrationId());
    }
    return getTodaysEmailDigest();
  };

  const getDigestByDate = async (date, mailIntegrationId) => {
    const isCurrent = captureCurrentView();
    try {
      const formattedDate = dayjs(date).format("MMMM D, YYYY");
      const digestResponse = await getDigestByTitleService(
        getToken, userId, formattedDate, mailIntegrationId,
      );
      if (!isCurrent()) return;
      if (digestResponse) {
        await loadDigest(digestResponse, isCurrent);
      } else {
        throw new Error('Saved digest not found');
      }
    } catch (error) {
      if (isCurrent()) {
        setSyncError({ code: null });
        setSavedLoadError(true);
      }
    }
  }

  const generateEmailDigest = async (options = {}) => {
    if (generateEmailDigestStarted.current) return false;
    // An explicit sync supersedes any pending saved-state bootstrap.
    if (!options?.isCurrent) viewGenerationRef.current += 1;
    const preserveFailure = options?.preserveFailure === true;
    const isCurrent = options?.isCurrent ?? captureCurrentView();
    const requestedMailIntegrationId = typeof options?.mailIntegrationId === 'string'
      && options.mailIntegrationId.length > 0
      ? options.mailIntegrationId
      : null;
    generateEmailDigestStarted.current = true;
    setIsLoadingDigest(true);
    if (!preserveFailure) setSyncError(null);
    setSelectedEmail(null);

    try {
      const response = await generateEmailDigestService(
        requestedMailIntegrationId
          ? { mailIntegrationId: requestedMailIntegrationId }
          : undefined,
      );
      if (!isCurrent()) return false;
      if (
        requestedMailIntegrationId
        && response?.mailbox?.id !== requestedMailIntegrationId
      ) {
        throw new Error('Digest mailbox identity changed during recovery');
      }

      const today = dayjs().format("MMMM D, YYYY");
      const digestData = {
        title: today,
        emailSummary: response.summary,
        labels: response.labels,
        mailbox: response.mailbox,
        destinations: response.destinations,
        content: response.content,
      };

      // Check if digest exists in DB
      const existingDigest = await getDigestByTitleService(
        getToken,
        userId,
        digestData.title,
        digestData.mailbox.id,
      );
      if (!isCurrent()) return false;

      if (!existingDigest) {
        // Create a new digest and store its emails
        digestData.id = `digest_${Math.random()
          .toString(36)
          .substring(2, 15)}`;
        digestData.step = "created";

        const newDigest = await addDigestService(
          getToken,
          userId,
          digestData
        );
        await addDigestEmailsService(
          getToken,
          newDigest.id,
          digestData.content
        );

        if (!isCurrent()) return false;
        dispatch(addOrSyncDigest(digestData));
      } else {
        // Update digest data object with existing digest data
        digestData.id = existingDigest.id;
        digestData.taskSummary = existingDigest.taskSummary;
        digestData.step = "synced";

        await updateDigestService(getToken, existingDigest.id, digestData);
        const existingEmails = await getDigestEmailsService(
          getToken,
          existingDigest.id
        );

        // Find new and updated emails
        const newEmails = digestData?.content?.filter(
          (email) => !existingEmails.some((e) => e.email_id === email.id)
        );
        const updatedEmails = digestData?.content?.filter((email) =>
          existingEmails.some((e) => e.email_id === email.id)
        );

        // Add new emails and update existing emails
        await addDigestEmailsService(getToken, existingDigest.id, newEmails);
        await updateDigestEmailsService(
          getToken,
          existingDigest.id,
          updatedEmails
        );

        // The generated batch only contains inbox mail. Reload the persisted
        // digest before rendering so already-processed mail remains visible.
        digestData.content = await getDigestEmailsService(getToken, existingDigest.id);
        if (!isCurrent()) return false;
        dispatch(addOrSyncDigest(digestData));
      }
      setSyncError(null);
      return digestData;
    } catch (error) {
      if (!isCurrent()) return false;
      console.error('Mailbox digest sync failed', {
        code: error?.response?.data?.code ?? 'unknown',
      });
      setSyncError(pickSafeMailFailureFields(error?.response?.data) ?? {
        code: null,
        provider: null,
        retryable: null,
      });
      setSyncFailureVersion((version) => version + 1);
      return false;
    } finally {
      generateEmailDigestStarted.current = false;
      if (isCurrent()) setIsLoadingDigest(false);
    }
  };

  const trustedDigestProvider = ['google', 'microsoft'].includes(currentDigest?.mailbox?.provider)
    ? currentDigest.mailbox.provider
    : null;
  const mailFailurePresentation = syncError
    ? presentMailFailure(
        syncError,
        trustedDigestProvider ? { expectedProvider: trustedDigestProvider } : {},
      )
    : null;

  const executeMailFailureRecovery = async (presentation, retry, mailIntegrationId = null) => {
    if (!presentation) return false;
    if (mailRecoveryStarted.current) return null;

    mailRecoveryStarted.current = true;
    setIsRecoveringMailFailure(true);
    try {
      return await runMailFailureRecovery({
        recoveryIntent: presentation.recoveryIntent,
        user,
        mailIntegrationId,
        retry,
      });
    } catch (_error) {
      console.error('Mailbox recovery failed');
      return false;
    } finally {
      mailRecoveryStarted.current = false;
      setIsRecoveringMailFailure(false);
    }
  };

  const recoverMailFailure = async () => {
    if (savedLoadError) return retrySavedDigest();
    const failedMailboxId = getDigestMailIntegrationId();
    const hasFailedMailbox = typeof failedMailboxId === 'string' && failedMailboxId.length > 0;
    const recovered = await executeMailFailureRecovery(
      mailFailurePresentation,
      () => generateEmailDigest(hasFailedMailbox
        ? {
            preserveFailure: true,
            mailIntegrationId: failedMailboxId,
          }
        : { preserveFailure: true }),
      hasFailedMailbox ? failedMailboxId : null,
    );
    if (recovered === false) setSyncFailureVersion((version) => version + 1);
    return recovered;
  };

  const recoverAsyncMailFailure = (presentation, failedMailboxId) => executeMailFailureRecovery(
    presentation,
    async () => {
      if (typeof failedMailboxId !== 'string' || failedMailboxId.length === 0) return false;
      const refreshedDigest = await generateEmailDigest({
        preserveFailure: true,
        mailIntegrationId: failedMailboxId,
      });
      if (!refreshedDigest || refreshedDigest.mailbox?.id !== failedMailboxId) return false;
      return processAllEmails({
        preserveFailures: true,
        digest: refreshedDigest,
        items: refreshedDigest.content ?? [],
      });
    },
    failedMailboxId,
  );

  const getEmailContent = async (emailId) => {
    try {
      const response = await getEmailContentService(getToken, {
        emailId,
        mailIntegrationId: getDigestMailIntegrationId(),
      })
        .then((response) => {
          return response;
        })
        .catch((error) => {
          const errorMessage = `Error getting Email content response: ${error}`;
          console.error(errorMessage);
          return errorMessage;
        });

      const emailWithContent = mergeEmailContentResponse(
        currentDigest?.content.find((email) => email.id === emailId),
        response
      );

      setSelectedEmail(emailWithContent);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching email content:", error);
    }
  };

  const handleMoveEmail = async (email, destinationKey) => {
    const blocked = refuseMailWrite('move_email', getMailWriteContext({ currentDigest, email }));
    if (blocked) return blocked;

    await triggerMoveEmailAction(email.id, destinationKey);
  };

  const triggerMoveEmailAction = async (emailId, destinationKey) => {
    const destinationLabel = currentDigest?.destinations?.find(({ key }) => key === destinationKey)?.label;
    const emailActionData = {
      type: "move-to-label",
      actor: "user",
      reason: "Moved manually by user",
      metaData: {
        title: "Moved manually by user",
        destinationKey,
        destinationLabel,
      },
    };

    try {
      const result = await moveEmail({
        emailId,
        mailIntegrationId: getDigestMailIntegrationId(),
        destinationKey,
        emailActionData,
        currentDigest,
        getToken,
      });
      if (result?.blocked) return;

      // Update digest with moved label
      dispatch(
        updateDigest({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === emailId
              ? {
                  ...item,
                  triageDestination: destinationKey,
                  archived: false,
                  actions: [
                    ...(item.actions || []),
                    {
                      type: "move-to-label",
                      actor: emailActionData.actor || "user",
                      reason:
                        emailActionData.reason || "Moved manually by user",
                      metaData:
                        {
                          title:
                            emailActionData.metaData?.title ||
                            "Moved manually by user",
                          content:
                            emailActionData.metaData?.content ||
                            "You moved the email to the for review label",
                          destinationKey,
                          destinationLabel,
                        } || [],
                    },
                  ],
                }
              : item
          ),
        })
      );

      // Hide email actions if needed
      setIsVisible((prev) => ({
        ...prev,
        [emailId]: false,
      }));
    } catch (error) {
      console.error("Failed to move email:", error);
      alert("Failed to move email");
    }
  };

  const handleArchiveEmail = async (email) => {
    const blocked = refuseMailWrite('archive_email', getMailWriteContext({ currentDigest, email }));
    if (blocked) return blocked;

    await triggerArchiveEmailAction(email.id);
    setSelectedEmail(null);
  };

  const triggerArchiveEmailAction = async (emailId) => {
    const emailActionData = {
      type: "archive",
      actor: "user",
      reason: "Archived manually by user",
      metaData: {
        title: "Archived manually by user",
      },
    };

    try {
      const result = await archiveEmail({
        emailId,
        mailIntegrationId: getDigestMailIntegrationId(),
        emailActionData,
        currentDigest,
        getToken,
      });
      if (result?.blocked) return;

      // Update digest with archived status
      dispatch(
        updateDigest({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === emailId
              ? {
                  ...item,
                  archived: true,
                  actions: [
                    ...(item.actions || []),
                    {
                      type: "archive",
                      actor: emailActionData.actor || "user",
                      reason:
                        emailActionData.reason || "Archived manually by user",
                      metaData:
                        {
                          title: emailActionData.metaData?.title || "",
                          content: emailActionData.metaData?.content || "",
                        } || [],
                    },
                  ],
                }
              : item
          ),
        })
      );

      // Hide email actions if needed
      setIsVisible((prev) => ({
        ...prev,
        [emailId]: false,
      }));

      setIsCardVisible((prev) => ({
        ...prev,
        [emailId]: false,
      }));
    } catch (error) {
      console.error("Failed to archive email:", error);
    }
  };

  const handleReplyToEmail = async (email, replyText) => {
    const blocked = refuseMailWrite('create_reply_draft', getMailWriteContext({ currentDigest, email }));
    if (blocked) return blocked;

    await triggerReplyToEmail(email, replyText);
    setSelectedEmail(null);
  };

  const triggerReplyToEmail = async (email, replyText) => {
    if (inputValues[email.id]) {
      setInputValues({ ...inputValues, [email.id]: "" });
    }

    const emailActionData = {
      type: "reply",
      actor: "user",
      reason: "Created a reply draft for review",
      metaData: {
        title: "Created a reply draft for review",
        content: replyText,
      },
    };

    try {
      const result = await replyToEmail({
        email,
        mailIntegrationId: getDigestMailIntegrationId(),
        replyText,
        emailActionData,
        currentDigest,
        getToken,
      });
      if (result?.blocked) return;

      // Update digest with draft reply status
      dispatch(
        updateDigest({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === email.id
              ? {
                  ...item,
                  actions: [
                    ...(item.actions || []),
                    {
                      type: "reply",
                      actor: emailActionData.actor || "user",
                      reason:
                        emailActionData.reason ||
                        "Created a reply draft for review",
                      metaData:
                        {
                          title:
                            emailActionData.metaData?.title ||
                            "Created a reply draft for review",
                          content:
                            emailActionData.metaData?.content || replyText,
                        } || [],
                    },
                  ],
                }
              : item
          ),
        })
      );

      // Clear input
      setInputValues((prev) => ({
        ...prev,
        [email.id]: "",
      }));

      // Hide reply input if needed
      setIsVisible((prev) => ({
        ...prev,
        [email.id]: false,
      }));
    } catch (error) {
      console.error("Failed to reply to email:", error);
      // Optionally show error to user
    }
  };

  const handleForward = async (email, replyText, forwardTo) => {
    const blocked = refuseMailWrite('create_forward_draft', getMailWriteContext({ currentDigest, email }));
    if (blocked) return blocked;

    await forwardEmail(email, replyText, forwardTo);
    setSelectedEmail(null);
  };

  const forwardEmail = async (email, replyText, forwardTo) => {
    try {
      const token = await getToken();
      const response = await axios
        .post(
          `${process.env.REACT_APP_API_URL}/api/email/forward_email`,
          {
            emailId: email.id,
            mailIntegrationId: getDigestMailIntegrationId(),
            toEmailAddresses: [forwardTo],
            subject: email.subject,
            replyBody: replyText,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Error forwarding email:", error);
          return error;
        });

      if (!response.data.id) {
        throw new Error("Failed to forward email");
      }

      dispatch(
        updateDigest({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === email.id ? { ...item, forwarded: true } : item
          ),
        })
      );

      setIsVisible((prev) => ({
        ...prev,
        [email.id]: false,
      }));
    } catch (error) {
      console.error("Error forwarding email:", error);
    }
  };

  const handleCreateTask = async (email) => {
    await triggerCreateTask(email);
    setSelectedEmail(null);
  };

  const triggerCreateTask = async (email) => {
    const emailActionData = {
      type: "create-a-task",
      actor: "user",
      reason: "Created a task manually by user",
      metaData: {
        title: `Email Task: ${email.subject}`,
        content: `Email summary: ${email.actions
          ?.map((action) => action?.summary)
          .join("\n\n")}`,
      },
    };

    try {
      const taskId = await createEmailTask({
        email,
        dispatch,
        emailActionData,
        createTaskAction: createTask,
        getToken,
        userId,
        mailIntegrationId: getDigestMailIntegrationId(),
      });

      // Update digest with draft reply status
      dispatch(
        updateDigest({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === email.id
              ? {
                  ...item,
                  actions: [
                    ...(item.actions || []),
                    {
                      type: "create-a-task",
                      actor: emailActionData.actor || "user",
                      reason:
                        emailActionData.reason ||
                        "Created a task manually by user",
                      metaData:
                        {
                          title: emailActionData.metaData?.title || "",
                          content: emailActionData.metaData?.content || "",
                          taskId: taskId,
                        } || [],
                    },
                  ],
                }
              : item
          ),
        })
      );

      // Clear input
      setInputValues((prev) => ({
        ...prev,
        [email.id]: "",
      }));

      // Hide reply input if needed
      setIsVisible((prev) => ({
        ...prev,
        [email.id]: false,
      }));
    } catch (error) {
      console.error("Failed to reply to email:", error);
      // Optionally show error to user
    }
  };

  const handleViewTask = async (email) => {
    dispatch(
      setCurrentTask(
        email.actions?.find((action) => action.metaData?.taskId)?.metaData?.taskId
      )
    );
    navigate(
      `/task/${
        email.actions?.find((action) => action.metaData?.taskId)?.metaData?.taskId
      }`
    );
  };

  const handleViewProject = async (email) => {
    dispatch(
      setCurrentProject(
        email.actions?.find(action => action.metaData?.projectId)?.metaData?.projectId
      )
    );
    navigate(
      `/project/${
        email.actions?.find(action => action.metaData?.projectId)?.metaData?.projectId
      }`
    );
  };

  const handleAddEmailToProject = async (email, projectId, projectTitle) => {
    const selectedChat = chats.find(chat => chat.projectId === projectId);
    if (!selectedChat) {
      console.error('No matching project found');
      return;
    }

		const emailActionData = {
			actor: 'user',
			reason: 'Added to project manually by user',
			metaData: {
				title: projectTitle,
				content: '',
				projectId: projectId,
			}
		};

    try {
      await addEmailToProject({
        emailId: email.id,
        mailIntegrationId: getDigestMailIntegrationId(),
        vectorStoreId: selectedChat.vectorStoreId,
        dispatch,
        chats,
        getToken,
		    emailActionData,
				currentDigest,
        addEmailFileIdAction: addEmailFileId,
        updateDigestAction: updateDigest
      });

      // TODO: Set the visibility of the email to false

      // Clear input
      setInputValues((prev) => ({
        ...prev,
        [email.id]: "",
      }));

      // Hide reply input if needed
      setIsVisible((prev) => ({
        ...prev,
        [email.id]: false,
      }));

      setIsProjectModalOpen(false);
      showNotification('Email added to project successfully!');
    } catch (error) {
      console.error('Error adding email to project:', error);
      showNotification("We couldn't add that email to the project. Please try again.", 'error');
    }
  }

  const handleReadOnlyModeAcknowledgeAction = async (email) => {
    if (email.actions?.some(action => action.type === 'archive')) {
          await archiveEmail({
            emailId: email.id,
            mailIntegrationId: getDigestMailIntegrationId(),
            emailActionData: email.actions?.find(action => action.type === 'archive'),
        currentDigest,
        getToken,
      });
    }
    else if (email.actions?.some(action => action.type === 'move-to-label')) {
      const moveAction = email.actions.find(action => action.type === 'move-to-label');
      const destinationKey = resolveActionDestination(moveAction, currentDigest)?.key;

      if (!destinationKey) {
        throw new Error("We couldn't work out where this email was filed. Sync your inbox and try again.");
      }

          await moveEmail({
            emailId: email.id,
            mailIntegrationId: getDigestMailIntegrationId(),
            destinationKey,
        emailActionData: moveAction,
        currentDigest,
        getToken,
      });
    }
  }

  const acknowledgeAction = async (emailId) => {
    const acknowledgedEmail = {
      ...currentDigest.content.find((item) => item.id === emailId),
      archived: true,
    };

    try {
      if (readOnlyMode) {
        await handleReadOnlyModeAcknowledgeAction(acknowledgedEmail);
      }

      await updateDigestEmailsService(getToken, currentDigest.id, [
        {
          ...acknowledgedEmail,
          archived: true,
        },
      ]);
    } catch (error) {
      console.error('Error acknowledging email action:', error);
      showNotification(
        error?.message || "We couldn't complete that review. Please try again.",
        'error'
      );
      return;
    }

    dispatch(
      updateDigest({
        ...currentDigest,
        content: currentDigest.content.map((item) =>
          item.id === emailId ? { ...item, archived: true } : item
        ),
      })
    );

    setIsCardVisible((prev) => ({
      ...prev,
      [emailId]: false,
    }));
  };

  const handleUndoAcknowledge = async (email) => {
    const emailId = email?.id;
    if (!emailId) return;

    const acknowledgedEmail = currentDigest?.content?.find((item) => item.id === emailId) || email;
    const mailWriteContext = getMailWriteContext({ currentDigest, email: acknowledgedEmail });
    const mailboxWasMutated = Boolean(
      acknowledgedEmail?.actions?.some(
        (action) => action.type === 'archive' || action.type === 'move-to-label'
      )
    ) && canWriteMail(mailWriteContext, 'restore_email');

    try {
      if (mailboxWasMutated) {
        await restoreAcknowledgedEmailToInbox(acknowledgedEmail);
      }

      await updateDigestEmailsService(getToken, currentDigest.id, [
        {
          ...acknowledgedEmail,
          archived: false,
        },
      ]);
    } catch (error) {
      console.error('Error undoing review completion:', error);
      showNotification("We couldn't undo that review. Please try again.", 'error');
      return;
    }

    dispatch(
      updateDigest({
        ...currentDigest,
        content: currentDigest.content.map((item) =>
          item.id === emailId ? { ...item, archived: false } : item
        ),
      })
    );

    setIsCardVisible((prev) => ({
      ...prev,
      [emailId]: true,
    }));
    setSelectedEmail(null);
  };

  const restoreAcknowledgedEmailToInbox = async (email) => {
    const blocked = refuseMailWrite('restore_email', getMailWriteContext({ currentDigest, email }));
    if (blocked) return blocked;

    return restoreEmailToInboxService(getToken, {
      emailId: email.id,
      mailIntegrationId: getDigestMailIntegrationId(),
    });
  };

  const undoAutomatedAction = async (emailId, actionType) => {
    const email = currentDigest.content.find((item) => item.id === emailId);
    const aiActions = getAssistantActions(email.actions);

    for (const action of aiActions) {
      if (action.type !== actionType) {
        continue;
      }

      switch (action.type) {
        case 'archive':
          await undoAutomatedArchive(email);
        break;
        case 'reply':
          // console.log('undoing automated reply to email');
          // TODO: Implement undo reply to email
          // Delete the Draft Reply from Gmail
          // Delete the Email Action from the DB
          // await replyToEmail(email);
          break;
        case 'move-to-label':
          await undoAutomatedMoveToLabel(email);
          break;
        case 'create-a-task':
          await undoAutomatedCreateTask(email);
          break;
        case 'add-to-project':
          // console.log('undo add email to project');
          // TODO: Implement undo add email to project
          // Delete the Email Action from the DB
          // await addEmailToProject(email);
          break;
        default:
          break;
      }
    }
  }

  const restoreEmailToInbox = async (email) => {
    const blocked = refuseMailWrite('restore_email', getMailWriteContext({ currentDigest, email }));
    if (blocked) return blocked;

    await restoreEmailToInboxService(getToken, {
      emailId: email.id,
      mailIntegrationId: getDigestMailIntegrationId(),
    });
  }

  const undoAutomatedArchive = async (email) => {
    await restoreEmailToInbox(email);

    await deleteEmailActionByTypeService(getToken, {
      emailId: email.id,
      emailActionType: 'archive',
      mailIntegrationId: getDigestMailIntegrationId(),
    });

    dispatch(
      updateDigest({
        ...currentDigest,
        content: currentDigest.content.map((item) =>
          item.id === email.id ? {
            ...item,
            archived: false,
            actions: item.actions.filter(action => action.type !== 'archive')
          } : item
        ),
      })
    );
  }

  const undoAutomatedMoveToLabel = async (email) => {
    await restoreEmailToInbox(email);

    await deleteEmailActionByTypeService(getToken, {
      emailId: email.id,
      emailActionType: 'move-to-label',
      mailIntegrationId: getDigestMailIntegrationId(),
    });
    await deleteEmailActionByTypeService(getToken, {
      emailId: email.id,
      emailActionType: 'archive',
      mailIntegrationId: getDigestMailIntegrationId(),
    });

    dispatch(
      updateDigest({
        ...currentDigest,
        content: currentDigest.content.map((item) =>
          item.id === email.id ? {
            ...item,
            archived: false,
            labels: [],
            actions: item.actions.filter(action => action.type !== 'move-to-label' && action.type !== 'archive')
          } : item
        ),
      })
    );
  }

  const undoAutomatedCreateTask = async (email) => {
    const taskId = email.actions?.find(action => action.type === 'create-a-task')?.metaData?.taskId;

    await deleteTaskService(getToken, taskId)
    await deleteEmailActionByTypeService(getToken, {
      emailId: email.id,
      emailActionType: 'create-a-task',
      mailIntegrationId: getDigestMailIntegrationId(),
    });
    await deleteEmailActionByTypeService(getToken, {
      emailId: email.id,
      emailActionType: 'archive',
      mailIntegrationId: getDigestMailIntegrationId(),
    });

    const taskChat = chats.find(chat => chat.taskId === taskId);
    if (taskChat) {
      dispatch(deleteThread({ chatId: taskChat.id }));
      await deleteChatService(getToken, taskChat.id);
      await deleteTaskFileService(getToken, { fileId: taskChat.fileId });
    }

    await new Promise((resolve) => {
      dispatch(deleteTask(taskId))
      // Give Redux a chance to update the state
      setTimeout(() => {
        resolve();
      }, 250);
    }).then(() => {
      dispatch(
        updateDigest({
          ...currentDigest,
          content: currentDigest.content.map((item) =>
            item.id === email.id ? { 
              ...item, 
              actions: item.actions.filter(action => action.type !== 'create-a-task' && action.type !== 'archive')
            } : item
          ),
        })
      );
    });
  }

  const handleStartEmailAutomation = () => {
    if (unknownReadOnlyMode) {
      showEmailTriageOnboardingModal();
      return;
    }
    processAllEmails();
  };

  const getActiveTask = (email) => {
    return (email.actions?.length > 0 && email.actions?.filter(action => action.type === 'create-a-task').length !== 0) &&
      (email.actions?.filter(action => action.metaData?.taskId).length !== 0);
  }

  const getActiveProject = (email) => {
    return (email.actions?.length > 0 && email.actions?.filter(action => action.type === 'add-to-project' && action.metaData?.projectId).length !== 0);
  }

  const showEmailTriageOnboardingModal = () => {
    setIsEmailTriageOnboardingModalOpen(true)
  }

  const handleToggleReadOnlyMode = async (newReadOnlyMode) => {
    setReadOnlyMode(newReadOnlyMode)
    setUnknownReadOnlyMode(false)
    
    try {
      await updateUserEmailActionReadOnlyService({ enabled: newReadOnlyMode })
      showNotification('Read Only Mode Has Been Updated!');
    } catch (error) {
      console.error('Error updating read only mode:', error)
      showNotification("We couldn't update Read Only Mode. Please try again.", 'error');
    }
  }

  const showScheduleUpdateNotification = () => {
    showNotification('Schedule Has Been Updated!');
  }

  const RenderDigestDetails = () => {
    return (
      <>
        <div className="flex justify-between items-center mb-5 py-2 border-b border-slate-200">
          <div className="flex flex-row justify-between w-full mt-1">
            <h1 className="font-bold text-slate-700">
              <DigestDatePicker />
            </h1>
            <div className="flex flex-col items-end justify-center leading-tight text-left gap-0">
              <span className="text-sm text-slate-700">{totalDigestItems || 0} Email{totalDigestItems !== 1 ? 's' : ''} </span>
              <span className="text-sm text-slate-500 font-normal">Last Updated: {formatEmailDate(currentDigest?.updated_at)}</span>
            </div>
          </div>
        </div>

        <section className="flex items-center gap-2">
          <div className="flex w-full max-h-[83vh]">
            <div className="flex flex-col w-full relative">
              <DigestEmailSummary
                generateEmailDigest={generateEmailDigest}
                remainingDigestItems={remainingDigestItems}
                currentEmailProcessingIndex={currentEmailProcessingIndex}
                isLoadingDigest={isLoadingDigest}
                isDigestSummaryOpen={isDigestSummaryOpen}
                setIsDigestSummaryOpen={setIsDigestSummaryOpen}
                isProcessingEmailActions={isProcessingEmailActions}
                selectedEmail={selectedEmail}
                handleStartEmailAutomation={handleStartEmailAutomation}
                getAllEmails={getAllEmails}
                readOnlyMode={readOnlyMode}
              />
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <div ref={containerRef}
      className={`w-full flex ${getIsMobileOnly() ? 'flex-col' : 'flex-row'}`}
    >
      {getIsMobileOnly() ? (
        <div className={`overflow-y-auto px-4 ${isDigestSummaryOpen ? 'h-3/5' : 'h-fit'}`}>
          {RenderDigestDetails()}
        </div>
      ) : (
        <Resizable
          size={{ width: leftWidth || containerWidth * DEFAULT_LEFT_RATIO, height: '100%' }}
          minWidth={Math.max(MIN_LEFT_WIDTH, containerWidth * MIN_LEFT_PERCENTAGE)}
          maxWidth={containerWidth * 0.7}
          enable={{ right: true }}
          onResizeStop={handleResizeStop}
          handleComponent={{
            right: <div className="w-1 h-full hover:bg-stone-300 cursor-ew-resize absolute right-0 top-0 z-10 transition-colors duration-200" />
          }}
          handleStyles={{
            right: {
              right: '-1px'
            }
          }}
        >
          <div className="px-4 h-full overflow-y-auto">
            {RenderDigestDetails()}
          </div>
        </Resizable>
      )}

      <div className={`flex flex-col w-full ${getIsMobileOnly() ? 'px-4' : 'pr-4'} ${(getIsMobileOnly()) ? !isDigestSummaryOpen ? 'h-3/4' : 'h-2/5' : 'h-full'}`}> 
        <div className={`flex flex-col w-full h-full bg-white rounded-xl my-3 mx-0 px-4 pt-2 shadow-xl border border-slate-200 transition-all duration-500 overflow-y-auto`}>
          {selectedEmail ? (
            <EmailContent
              email={selectedEmail}
              onClose={() => setSelectedEmail(null)}
              isOpen={isModalOpen}
              isProjectModalOpen={isProjectModalOpen}
              setIsProjectModalOpen={setIsProjectModalOpen}
              onReply={handleReplyToEmail}
              onArchive={handleArchiveEmail}
              onForward={handleForward}
              onMoveEmail={handleMoveEmail}
              onCreateTask={handleCreateTask}
              onViewTask={handleViewTask}
              onViewProject={handleViewProject}
              onAddEmailToProject={handleAddEmailToProject}
              getActiveTask={getActiveTask}
              getActiveProject={getActiveProject}
              readOnlyMode={readOnlyMode}
              handleUndoAutomatedAction={undoAutomatedAction}
              handleAcknowledgeAction={acknowledgeAction}
            />
          ) : (
            <FilteredEmailView
              estimatedEmailTime={estimatedEmailTime}
              generateEmailDigest={generateEmailDigest}
              isLoadingDigest={isLoadingDigest}
              totalDigestItems={totalDigestItems}
              remainingDigestItems={remainingDigestItems}
              handleUndoAutomatedAction={undoAutomatedAction}
              acknowledgeAction={acknowledgeAction}
              handleUndoAcknowledge={handleUndoAcknowledge}
              getEmailContent={getEmailContent}
              isCardVisible={isCardVisible}
              inputValues={inputValues}
              setInputValues={setInputValues}
              setIsCardVisible={setIsCardVisible}
              isProjectModalOpen={isProjectModalOpen}
              setIsProjectModalOpen={setIsProjectModalOpen}
              isProcessingEmailActions={isProcessingEmailActions}
              isProcessingEvents={isProcessingEvents}
              currentEmailProcessingIndex={currentEmailProcessingIndex}
              getAllEmails={getAllEmails}
              itemsToRender={itemsToRender}
              handleArchiveEmail={handleArchiveEmail}
              handleMoveEmail={handleMoveEmail}
              handleCreateTask={handleCreateTask}
              handleViewTask={handleViewTask}
              handleReplyToEmail={handleReplyToEmail}
              handleAddEmailToProject={handleAddEmailToProject}
              getActiveTask={getActiveTask}
              getActiveProject={getActiveProject}
              handleViewProject={handleViewProject}
              readOnlyMode={readOnlyMode}
              setReadOnlyMode={setReadOnlyMode}
              showEmailTriageOnboardingModal={showEmailTriageOnboardingModal}
              showScheduleUpdateNotification={showScheduleUpdateNotification}
              activeMailbox={currentDigest?.mailbox}
              syncError={syncError}
              onRetrySync={savedLoadError ? retrySavedDigest : generateEmailDigest}
              mailFailurePresentation={mailFailurePresentation}
              mailFailureIdentity={mailFailurePresentation
                ? `${mailFailurePresentation.identity}:${syncFailureVersion}`
                : null}
              onRecoverMailFailure={recoverMailFailure}
              onRecoverAsyncMailFailure={recoverAsyncMailFailure}
              isRecoveringMailFailure={isRecoveringMailFailure}
            />
          )}
        </div>
      </div>

      <EmailTriageOnboardingModal
        isOpen={isEmailTriageOnboardingModalOpen}
        onClose={() => setIsEmailTriageOnboardingModalOpen(false)}
        onToggleReadOnlyMode={handleToggleReadOnlyMode}
        readOnlyMode={readOnlyMode}
      />

      {isNotificationVisible && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div
            role="alert"
            data-severity={notificationSeverity}
            className={`text-white p-4 rounded-md text-lg font-bold ${
              notificationSeverity === 'error' ? 'bg-red-600' : 'bg-green-500'
            }`}
          >
            {notificationMessage}
          </div>
        </div>
      )}
    </div>
  );
}
